import * as THREE from 'three';
import { isSketchContour, isSketchGroup, type SketchContour } from '../../../mesh';
import { sampleProfileRegionRingPoints3 } from '../../../utils/feature/offsetProfileCurveSegment';
import { buildProfileCurveSegmentsFromSketchContour } from '../../../utils/feature/profileCurveSegments';
import type { ExtrudeFeaturePrototype } from '../../prototype/extrude/ExtrudeFeaturePrototype';

/**
 * 拉伸场景轮廓。
 */
export interface BossExtrudeSceneContour {
  /** 轮廓 ID。 */
  id: string;
  /** 草图 ID。 */
  sketchId: string;
  /** 轮廓源对象运行时 ID。 */
  sourceObjectId?: string;
  /** 轮廓源对象跨会话稳定持久 ID；特征绑定的 contourKeys 原生记录该身份。 */
  sourcePersistentId?: string;
  /** 轮廓键。 */
  contourKey?: string;
  /** 区域几何。 */
  geometry: ProfileRegionGeometry;
  /** 相交闭合区域的稳定身份。 */
  regionIdentity?: PersistedRegionIdentity;
  /** 基点。 */
  basePoint: THREE.Vector3;
  /** 法向。 */
  normal: THREE.Vector3;
  /** 场景对象。 */
  sceneObject: THREE.Object3D | null;
}

/**
 * 拉伸轮廓选择解析结果。
 */
export type BossExtrudeSelectionResolution = BossExtrudeSceneContour[] | 'need-region' | 'open-sketch' | null;

/**
 * 拉伸场景轮廓解析器。
 *
 * 设计说明：
 * 1. 统一负责“从场景对象恢复拉伸可用轮廓”的语义；
 * 2. 只认显式对象能力：`uuid/name/objectId` 与 `SketchContour`；
 * 3. web-client 与 edit session 都应复用这条链，不再各自本地恢复轮廓。
 */
export class BossExtrudeSceneResolver {
  /**
   * 通过对象 ID 在场景中查找对象。
   *
   * @param scene 场景。
   * @param objectId 对象 ID。
   * @returns 场景对象。
   */
  public findSceneObjectById(scene: THREE.Scene | null, objectId: string): THREE.Object3D | null {
    if (!scene) {
      return null;
    }
    let target: THREE.Object3D | null = null;
    scene.traverse((object) => {
      const runtimeObjectId = (object as THREE.Object3D & { objectId?: string }).objectId;
      if (
        object.uuid === objectId
        || object.name === objectId
        || runtimeObjectId === objectId
      ) {
        target = object;
      }
    });
    return target;
  }

  /**
   * 从场景对象解析拉伸轮廓。
   *
   * @param scene 场景。
   * @param objectId 对象 ID。
   * @returns 轮廓解析结果。
   */
  public resolveContoursFromSelection(scene: THREE.Scene | null, objectId: string): BossExtrudeSelectionResolution {
    const sceneObject = this.findSceneObjectById(scene, objectId);
    if (!sceneObject) {
      return null;
    }

    if (isSketchContour(sceneObject)) {
      const region = this.buildContourFromSketchContour(sceneObject as never);
      return region ? [region] : null;
    }

    const embeddedContour = this.resolveEmbeddedContour(sceneObject);
    if (embeddedContour) {
      const region = this.buildContourFromSketchContour(embeddedContour as never);
      return region ? [region] : null;
    }

    const sketchGroup = this.resolveSketchGroup(sceneObject);
    if (!sketchGroup) {
      return null;
    }

    const contours = this.collectContoursFromGroup(sketchGroup);
    if (contours.length === 1) {
      return contours;
    }
    if (contours.length > 1) {
      return 'need-region';
    }
    return 'open-sketch';
  }

  /**
   * 从 prototype 恢复轮廓列表。
   *
   * @param scene 场景。
   * @param prototype 拉伸 prototype。
   * @returns 恢复后的轮廓列表。
   */
  public resolveContoursFromPrototype(
    scene: THREE.Scene | null,
    prototype: ExtrudeFeaturePrototype,
  ): BossExtrudeSceneContour[] {
    const bindingPayload = prototype
      .getBindings()
      .map((binding) => binding.serialize())
      .find((binding) => binding.kind === 'sketch-profile');
    const contourKeys = bindingPayload?.kind === 'sketch-profile'
      ? (bindingPayload.contourKeys ?? []).filter((item): item is string => typeof item === 'string')
      : [];
    const sketchId = bindingPayload?.kind === 'sketch-profile'
      ? bindingPayload.sketchId
      : prototype.getSourceSketchId();

    return this.resolveContoursFromProfileBinding(scene, sketchId, contourKeys);
  }

  /**
   * 按 `sketch-profile` 绑定回查场景轮廓。
   *
   * 设计说明：
   * 1. 绑定中的草图 ID 与 contourKeys 原生记录的是跨会话持久身份，场景对象持有的是运行时 uuid，
   *    因此草图组查找与轮廓匹配都必须同时认这两维身份，否则重开文档后编辑特征将取不到任何轮廓；
   * 2. 拉伸与旋转共用同一条 profile 绑定协议，回查逻辑在此单点收口，避免两处各自演化；
   * 3. 只做场景回查，不构建命令、不触发预览。
   *
   * @param scene 当前场景。
   * @param sketchId 绑定中的草图 ID，可能是持久 ID 或运行时 uuid。
   * @param contourKeys 绑定中的轮廓键序列。
   * @returns 按 contourKeys 顺序命中的轮廓集合。
   */
  public resolveContoursFromProfileBinding(
    scene: THREE.Scene | null,
    sketchId: string | null | undefined,
    contourKeys: readonly string[],
  ): BossExtrudeSceneContour[] {
    if (typeof sketchId !== 'string' || sketchId.length === 0 || contourKeys.length === 0) {
      return [];
    }

    const sketchGroup = this.findSketchGroupBySketchIdentity(scene, sketchId);
    if (!sketchGroup) {
      return [];
    }

    const latestContours = this.collectContoursFromGroup(sketchGroup);
    const usedContourIds = new Set<string>();
    return contourKeys.flatMap((contourKey) => {
      const matched = latestContours.find((contour) => {
        if (usedContourIds.has(contour.id)) {
          return false;
        }
        return contour.id === contourKey
          || contour.contourKey === contourKey
          || contour.sourceObjectId === contourKey
          || contour.sourcePersistentId === contourKey;
      });
      if (!matched) {
        return [];
      }
      usedContourIds.add(matched.id);
      return [matched];
    });
  }

  /**
   * 按草图身份在场景中查找草图组。
   *
   * @param scene 当前场景。
   * @param sketchId 草图 ID，可能是持久 ID 或运行时 uuid。
   * @returns 命中的草图组；未命中时返回 null。
   */
  private findSketchGroupBySketchIdentity(
    scene: THREE.Scene | null,
    sketchId: string,
  ): THREE.Object3D | null {
    if (!scene) {
      return null;
    }
    let target: THREE.Object3D | null = null;
    scene.traverse((object) => {
      if (target || !isSketchGroup(object)) {
        return;
      }
      const persistentId = (object as THREE.Object3D & { persistentId?: string }).persistentId;
      if (object.uuid === sketchId || persistentId === sketchId) {
        target = object;
      }
    });
    return target;
  }

  /**
   * 收集草图组下的全部轮廓。
   *
   * @param group 草图组。
   * @returns 轮廓列表。
   */
  public collectContoursFromGroup(group: THREE.Object3D): BossExtrudeSceneContour[] {
    const contours: BossExtrudeSceneContour[] = [];
    const usedContourIds = new Set<string>();
    group.traverse((child) => {
      // 拉伸 profile 属于草图定义数据，不能依赖上色草图轮廓是否仍挂在场景树中。
      // 因此这里优先使用现有 SketchContour，缺失时再通过闭合草图对象的统一能力懒恢复。
      const sketchContour = isSketchContour(child)
        ? child
        : this.resolveEmbeddedContour(child);
      if (!sketchContour) {
        return;
      }
      const region = this.buildContourFromSketchContour(sketchContour as never);
      if (!region || usedContourIds.has(region.id)) {
        return;
      }
      usedContourIds.add(region.id);
      contours.push(region);
    });
    return contours;
  }

  /**
   * 解析对象所属草图组。
   *
   * @param object 场景对象。
   * @returns 草图组。
   */
  private resolveSketchGroup(object: THREE.Object3D | null): THREE.Object3D | null {
    let current: THREE.Object3D | null = object;
    while (current) {
      if (isSketchGroup(current)) {
        return current;
      }
      current = current.parent;
    }
    return null;
  }

  /**
   * 从 SketchContour 构建轮廓抽象。
   *
   * @param contour SketchContour。
   * @returns 轮廓抽象。
   */
  private buildContourFromSketchContour(contour: {
    uuid: string;
    source?: { uuid?: string; referencePlane?: {
      normal: THREE.Vector3;
      xAxis: THREE.Vector3;
      yAxis: THREE.Vector3;
    } };
    getContourPoints?: () => THREE.Vector3[];
  } & THREE.Object3D): BossExtrudeSceneContour | null {
    const referencePlane = contour.source?.referencePlane;
    if (!referencePlane) {
      return null;
    }
    const exactSegments = buildProfileCurveSegmentsFromSketchContour(
      contour as unknown as SketchContour,
    );
    /**
     * 精确曲线段是拉伸凸台 profile 的权威定义；只有旧数据没有精确段时，才读取
     * `SketchContour` 的显示采样。这样上色 Mesh 暂时为空也不会阻断圆、椭圆、样条或槽口拉伸。
     */
    const displayPoints = exactSegments.length <= 0 && typeof contour.getContourPoints === 'function'
      ? contour.getContourPoints().map((point) => [point.x, point.y, point.z] as [number, number, number])
      : [];
    const points = sampleProfileRegionRingPoints3({
      points: displayPoints,
      segments: exactSegments.length > 0 ? exactSegments : undefined,
    });
    if (points.length < 3) {
      return null;
    }
    const sourceObject = contour.source instanceof THREE.Object3D ? contour.source : null;
    // 拉伸会话会用轮廓 ID 二次回查场景；上色轮廓被清理后，只有源草图对象 ID 仍然稳定存在。
    const sourceObjectId = typeof sourceObject?.uuid === 'string' ? sourceObject.uuid : undefined;
    // 特征绑定的 contourKeys 记录的是草图实体持久 ID，回查时必须带上这一维身份才能匹配上。
    const sourcePersistentId = (sourceObject as { persistentId?: unknown } | null)?.persistentId;
    const stableContourId = sourceObjectId ?? contour.uuid;
    const sketchGroup = this.resolveSketchGroup(contour) ?? this.resolveSketchGroup(sourceObject);
    const geometry: ProfileRegionGeometry = {
      outer: points,
      holes: [],
      normal: [referencePlane.normal.x, referencePlane.normal.y, referencePlane.normal.z],
      xAxis: [referencePlane.xAxis.x, referencePlane.xAxis.y, referencePlane.xAxis.z],
      yAxis: [referencePlane.yAxis.x, referencePlane.yAxis.y, referencePlane.yAxis.z],
      ...(exactSegments.length > 0 ? { outerSegments: exactSegments } : {}),
    };
    return {
      id: stableContourId,
      sketchId: sketchGroup?.uuid ?? '',
      sourceObjectId,
      sourcePersistentId: typeof sourcePersistentId === 'string' && sourcePersistentId.length > 0
        ? sourcePersistentId
        : undefined,
      contourKey: stableContourId,
      geometry,
      basePoint: this.computeCenter(points),
      normal: this.normalizeVector(new THREE.Vector3(...geometry.normal)),
      sceneObject: contour,
    };
  }

  /**
   * 解析对象自带的轮廓引用。
   *
   * @param object 场景对象。
   * @returns 内嵌轮廓对象。
   */
  private resolveEmbeddedContour(object: THREE.Object3D): THREE.Object3D | null {
    const directContour = (object as { contour?: THREE.Object3D }).contour;
    if (directContour && isSketchContour(directContour)) {
      return directContour;
    }
    const closedCollection = (object as { closedCollection?: THREE.Object3D }).closedCollection;
    if (closedCollection instanceof THREE.Object3D && closedCollection !== object) {
      const ownerContour = this.resolveEmbeddedContour(closedCollection);
      if (ownerContour) {
        return ownerContour;
      }
    }
    const contourGetter = (object as { getContour?: () => THREE.Object3D | null }).getContour;
    if (typeof contourGetter === 'function') {
      const contour = contourGetter.call(object);
      if (contour && isSketchContour(contour)) {
        return contour;
      }
    }
    const canCreateContour = (object as { canCreateContour?: () => boolean }).canCreateContour;
    const createContour = (object as { createContour?: () => THREE.Object3D | null }).createContour;
    if (typeof canCreateContour === 'function' && typeof createContour === 'function') {
      try {
        if (canCreateContour.call(object)) {
          const contour = createContour.call(object);
          if (contour && isSketchContour(contour)) {
            return contour;
          }
        }
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * 计算点列中心。
   *
   * @param points 点列。
   * @returns 中心点。
   */
  private computeCenter(points: Array<[number, number, number]>): THREE.Vector3 {
    const accumulator = points.reduce(
      (sum, point) => {
        sum.x += point[0];
        sum.y += point[1];
        sum.z += point[2];
        return sum;
      },
      new THREE.Vector3(),
    );
    return accumulator.multiplyScalar(1 / Math.max(points.length, 1));
  }

  /**
   * 归一化向量。
   *
   * @param vector 向量。
   * @returns 归一化结果。
   */
  private normalizeVector(vector: THREE.Vector3): THREE.Vector3 {
    if (vector.lengthSq() <= 1e-12) {
      return new THREE.Vector3(0, 0, 1);
    }
    return vector.normalize();
  }
}
